import React, { FC, useState } from 'react';
import { RangeDatePicker } from '@churchonline/calendar';
import { useRouter } from 'next/router';
import { DateUtils } from 'react-day-picker';
import { zeroHourISOString, finalHourISOString } from './Utils';
import { useTranslation } from 'react-i18next';
import { useLocalizedMonths } from '@lib/useLocalizedMonths';

interface ServicesInterface {
  from?: Date;
  to?: Date;
  enteredTo?: Date;
}

interface RangeProps {
  label?: string;
  width?: number;
  from?: Date;
  to?: Date;
  onClick: (from?: string, to?: string) => void;
  needPastDates?: boolean;
  fromMonth?: Date;
  disabledDays?: Array<{
    after: Date;
  }>;
  omitFilterButton?: boolean;
  timezone?: string;
}

interface FilterButtonProps {
  text: string;
  onClick: (from?: string, to?: string) => void;
  disabled: boolean;
}

export const RangePicker: FC<RangeProps> = ({
  from,
  label,
  needPastDates,
  omitFilterButton,
  onClick,
  timezone,
  to,
  width = 400,
  ...props
}) => {
  const [state, setState] = useState<ServicesInterface>({
    from,
    to,
    enteredTo: to,
  });
  const { t } = useTranslation();
  const { locale } = useRouter();

  const handleResetClick = (): void => {
    setState({
      from: undefined,
      to: undefined,
      enteredTo: undefined,
    });
  };

  const isSelectingFirstDay = (day: Date, from?: Date, to?: Date): boolean => {
    const isBeforeFirstDay = !!(from && DateUtils.isDayBefore(day, from));
    const isRangeSelected = !!(from && to);
    return !from || isBeforeFirstDay || isRangeSelected;
  };

  const handleSecondDaySelected = (day: Date): void => {
    setState({
      ...state,
      to: day,
    });
    if (omitFilterButton) {
      onClick(
        state.from && zeroHourISOString(state.from, timezone),
        day && zeroHourISOString(day, timezone)
      );
    }
  };

  const handleDayClick = (day: Date): void => {
    const { from, to } = state;
    if ((from && to && day >= from && day <= to) || DateUtils.isDayBefore(day, new Date())) {
      handleResetClick();
      return;
    }
    if (isSelectingFirstDay(day, from, to)) {
      setState({
        from: day,
        to: undefined,
        enteredTo: undefined,
      });
    } else {
      handleSecondDaySelected(day);
    }
  };

  const handlePastDayClick = (day: Date): void => {
    const { from, to } = state;
    if (from && to && day <= from && day >= to) {
      handleResetClick();
      return;
    }
    if (isSelectingFirstDay(day, from, to)) {
      setState({
        from: day,
        to: undefined,
        enteredTo: undefined,
      });
    } else {
      handleSecondDaySelected(day);
    }
  };

  const handleDayMouseEnter = (day: Date): void => {
    const { from, to } = state;
    if (!isSelectingFirstDay(day, from, to)) {
      setState({
        ...state,
        enteredTo: day,
      });
    }
  };

  const handleFilterClick = (): void => {
    return onClick(
      state.from && zeroHourISOString(state.from, timezone),
      state.to && finalHourISOString(state.to, timezone)
    );
  };

  const filterButtonProps = (): undefined | FilterButtonProps => {
    if (omitFilterButton) {
      return undefined;
    } else {
      return {
        text: t('filter'),
        onClick: handleFilterClick,
        disabled: !(state.from && state.to),
      };
    }
  };

  return (
    <RangeDatePicker
      button={filterButtonProps()}
      data-testid='datepicker'
      enteredTo={state.enteredTo}
      from={state.from}
      label={label}
      locale={locale}
      months={useLocalizedMonths()}
      onDayClick={needPastDates ? handlePastDayClick : handleDayClick}
      onDayMouseEnter={handleDayMouseEnter}
      width={width}
      {...props}
    />
  );
};
